import { utc } from "moment";
import { useEffect, useState } from "react";

import { ONE_MINUTE_MS } from "#/config/time";
import { useIsMaintenanceAlertDismissed } from "#/store/app/alerts/isMaintenanceAlertDismissed";

export interface ScheduledMaintenance {
  startTime: string;
  endTime: string;
  title?: string;
  description?: string;
}

export const scheduledMaintenances: ScheduledMaintenance[] = [
  {
    startTime: "2025-11-30T19:00:00Z",
    endTime: "2025-12-01T19:00:00Z",
  },
];

const getActiveMaintenance = () => {
  const now = utc();

  return scheduledMaintenances.find((maintenance) => {
    const start = utc(maintenance.startTime);
    const end = utc(maintenance.endTime);
    return now.isBetween(start, end, undefined, "[)");
  });
};

const checkVisibility = () => {
  return !getActiveMaintenance();
};

const getActiveMaintenanceDetails = () => {
  const activeMaintenance = getActiveMaintenance();

  if (!activeMaintenance) {
    return {
      title: "Scheduled Maintenance",
      description: "The system is currently under maintenance.",
    };
  }

  const start = utc(activeMaintenance.startTime);
  const end = utc(activeMaintenance.endTime);
  const isSameDay = start.isSame(end, "day");

  let description: string;

  if (isSameDay) {
    const startFormatted = start.format("h:mmA");
    const endFormatted = end.format("h:mmA");
    description = `The system will be unavailable between ${startFormatted} UTC and ${endFormatted} UTC.`;
  } else {
    const startFormatted = start.format("h:mmA [UTC on] DD/MM/YYYY");
    const endFormatted = end.format("h:mmA [UTC on] DD/MM/YYYY");
    description = `The system will be unavailable between ${startFormatted} and ${endFormatted}.`;
  }

  return {
    title: activeMaintenance.title ?? "Scheduled Maintenance",
    description: activeMaintenance.description ?? description,
  };
};

export const useMaintenanceAlertController = () => {
  const [isInvisible, setIsInvisible] = useState(() => checkVisibility());
  const [maintenanceDetails, setMaintenanceDetails] = useState(() =>
    getActiveMaintenanceDetails(),
  );

  const { isMaintenanceAlertDismissed, setIsMaintenanceAlertDismissed } =
    useIsMaintenanceAlertDismissed();

  useEffect(() => {
    const interval = setInterval(() => {
      setIsInvisible(checkVisibility());
      setMaintenanceDetails(getActiveMaintenanceDetails());
    }, ONE_MINUTE_MS);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const dismiss = () => {
    setIsMaintenanceAlertDismissed(true);
  };

  return {
    title: maintenanceDetails.title,
    description: maintenanceDetails.description,
    isInvisible: isInvisible || isMaintenanceAlertDismissed,
    isMaintenanceActive: !isInvisible,
    isMaintenanceAlertDismissed,
    dismiss,
  };
};
